import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse } from '../index'

// ==================== Bluetooth Status ====================

export interface BluetoothStatusResponse {
    enabled: number
    isConnected: number
    isAdvertising: number
    state: number
    deviceName: string
    macAddress: string
}

export async function getBluetoothStatus(clientId: string): Promise<BluetoothStatusResponse> {
    const response: Response = await fetch(`${HOST_URL}bluetooth/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<BluetoothStatusResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

// ==================== Enable/Disable Bluetooth ====================

export interface EnableBluetoothResponse {
    success: number
    errorMsg?: string
}

export async function enableBluetooth(clientId: string, enable: boolean): Promise<EnableBluetoothResponse> {
    const response: Response = await fetch(`${HOST_URL}bluetooth/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, enable: enable ? 1 : 0 })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<EnableBluetoothResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

// ==================== Device Name ====================

export interface GetDeviceNameResponse {
    deviceName: string
}

export interface SetDeviceNameResponse {
    success: number
    errorMsg?: string
}

export async function getBluetoothDeviceName(clientId: string): Promise<GetDeviceNameResponse> {
    const response: Response = await fetch(`${HOST_URL}bluetooth/device-name/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<GetDeviceNameResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw new Error(json.message)
    }

    return json.data
}

export async function setBluetoothDeviceName(clientId: string, deviceName: string): Promise<SetDeviceNameResponse> {
    const response: Response = await fetch(`${HOST_URL}bluetooth/device-name/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, deviceName })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<SetDeviceNameResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

// ==================== Advertising ====================

export interface StartAdvertisingResponse {
    success: number
    errorMsg?: string
}

export interface StopAdvertisingResponse {
    success: number
    errorMsg?: string
}

export async function startBluetoothAdvertising(clientId: string): Promise<StartAdvertisingResponse> {
    const response: Response = await fetch(`${HOST_URL}bluetooth/advertising/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<StartAdvertisingResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}

export async function stopBluetoothAdvertising(clientId: string): Promise<StopAdvertisingResponse> {
    const response: Response = await fetch(`${HOST_URL}bluetooth/advertising/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`)
    }

    const json: ApiResponse<StopAdvertisingResponse> = await response.json()
    if (response.status !== HttpStatus.Ok || json.data.success === 0) {
        throw new Error(json.data.errorMsg || json.message)
    }

    return json.data
}
